class Accordion extends HTMLElement {
  constructor() {
    super();
    this.lastToggled = 0;
    this.toggleDelay = 50;
    this.isNested = false;
  }
  /**
   * Lifecycle method called when the component is attached to the DOM.
   * Sets the initial display style and configures the accordion based on attributes.
   */
  connectedCallback() {
    this.style.display = "block";
    if (this.hasAttribute("open-first") && this.getAttribute("open-first") === "true") {
      const firstControl = this.querySelector(
        ".uwp-accordion__panel-control"
      );
      if (firstControl) {
        firstControl.classList.add("is-open");
      }
      const firstPanel = this.querySelector(
        ".uwp-accordion__panel-content"
      );
      if (firstPanel) {
        firstPanel.classList.add("is-open");
        this.calculateMaxHeight(firstPanel, true);
      }
    }
    if (this.hasAttribute("nested")) {
      this.isNested = true;
    }
    this.attachEventHandlers();
    this.setupAccessibility();
  }
  /**
   * Prevents too many events triggering an accordion panel in quick succession.
   * Mainly the overlap between focus and click events.
   * @param control - The element that functions as the control for an accordion panel.
   */
  debounceTogglePanel(control) {
    const now = Date.now();
    if (now - this.lastToggled > this.toggleDelay) {
      this.lastToggled = now;
      this.togglePanel(control);
    }
  }
  /**
   * Attaches click event handlers to the accordion.
   * Ensures that clicking a panel control toggles the corresponding panel.
   */
  attachEventHandlers() {
    this.addEventListener("click", (event) => {
      const target = event.target;
      if (target.classList.contains("uwp-accordion__panel-control")) {
        event.stopPropagation();
        this.debounceTogglePanel(target);
      }
    });
  }
  /**
   * Toggles the accordion panel open or closed.
   * Adjusts `max-height` to animate the opening or closing and sets accessibility attributes.
   *
   * @param control - The element that functions as the control for an accordion panel.
   */
  togglePanel(control) {
    const panel = control.nextElementSibling;
    const isOpen = panel.classList.contains("is-open");
    if (!isOpen) {
      panel.style.maxHeight = "100%";
    }
    panel.classList.toggle("is-open");
    control.classList.toggle("is-open");
    control.setAttribute("aria-expanded", !isOpen ? "true" : "false");
    if (isOpen) {
      panel.style.maxHeight = "0";
    }
    if (!this.isNested) {
      return;
    }
    panel.addEventListener(
      "transitionend",
      () => {
        if (!isOpen) {
          this.updateParentAccordions(panel);
        }
      },
      { once: true }
    );
  }
  /**
   * Calculates and sets the `max-height` for the accordion panel to either
   * fully show or hide its content.
   *
   * @param panel - The panel whose height is to be calculated and set.
   * @param open - A boolean indicating whether the panel should be opened
   * or closed.
   */
  calculateMaxHeight(panel, open) {
    panel.style.maxHeight = open ? `${panel.scrollHeight}px` : "0";
  }
  /**
   * Recursively updates the `max-height` of parent accordions to accommodate
   * changes in nested accordions.
   *
   * @param panel - The panel that was toggled which may affect its parent
   * accordion's size.
   */
  updateParentAccordions(panel) {
    var _a;
    let parent = panel.closest("uwp-accordion");
    while (parent && parent instanceof Accordion) {
      const parentPanel = parent.querySelector(
        ".uwp-accordion__panel-content"
      );
      if (parentPanel) {
        parentPanel.style.maxHeight = `${parentPanel.scrollHeight}px`;
      }
      parent = (_a = parent.parentElement) == null ? void 0 : _a.closest("uwp-accordion");
    }
  }
  /**
   * Sets up the accessibility attributes for the accordion, ensuring it can
   * be navigated using assistive technologies.
   */
  setupAccessibility() {
    const controls = this.querySelectorAll(".uwp-accordion__panel-control");
    controls.forEach((control, index) => {
      control.setAttribute("tabindex", "0");
      control.setAttribute("role", "button");
      control.setAttribute("aria-controls", `panel${index + 1}`);
      control.setAttribute("aria-expanded", "false");
      const panel = control.nextElementSibling;
      panel.setAttribute("id", `panel${index + 1}`);
      panel.setAttribute("role", "region");
      panel.setAttribute("aria-labelledby", control.id);
      control.id = `accordion-header-${index}`;
    });
  }
}
customElements.define("uwp-accordion", Accordion);
